import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as wishlistApi from '../api/wishlistApi';

const getGuestWishlist = () => {
  try {
    return JSON.parse(localStorage.getItem('rrc_guest_wishlist') || '[]');
  } catch {
    return [];
  }
};

const saveGuestWishlist = (items) => {
  localStorage.setItem('rrc_guest_wishlist', JSON.stringify(items));
};

export const fetchWishlist = createAsyncThunk(
  'wishlist/fetchWishlist',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      if (auth.isAuthenticated) {
        const data = await wishlistApi.fetchWishlist();
        return data;
      } else {
        const guestWishlist = getGuestWishlist();
        return { items: guestWishlist };
      }
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch wishlist');
    }
  }
);

export const toggleWishlist = createAsyncThunk(
  'wishlist/toggle',
  async (productId, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      if (auth.isAuthenticated) {
        const data = await wishlistApi.toggleWishlist(productId);
        return data;
      } else {
        let guestWishlist = getGuestWishlist();
        const index = guestWishlist.findIndex(
          (item) => item.productId === productId || item._id === productId
        );
        if (index > -1) {
          guestWishlist.splice(index, 1);
          saveGuestWishlist(guestWishlist);
          return { items: guestWishlist, added: false };
        } else {
          guestWishlist.push({ productId, _id: productId });
          saveGuestWishlist(guestWishlist);
          return { items: guestWishlist, added: true };
        }
      }
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to update wishlist');
    }
  }
);

export const checkInWishlist = createAsyncThunk(
  'wishlist/check',
  async (productId, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      if (auth.isAuthenticated) {
        const data = await wishlistApi.checkInWishlist(productId);
        return { productId, inWishlist: data.inWishlist };
      } else {
        const guestWishlist = getGuestWishlist();
        const inWishlist = guestWishlist.some(
          (item) => item.productId === productId || item._id === productId
        );
        return { productId, inWishlist };
      }
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState: {
    items: getGuestWishlist(),
    loading: false,
    error: null,
  },
  reducers: {
    clearWishlistError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWishlist.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || action.payload.data || [];
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(toggleWishlist.pending, (state) => {
        state.loading = true;
      })
      .addCase(toggleWishlist.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.items) {
          state.items = action.payload.items;
        } else if (action.payload.inWishlist !== undefined) {
          if (action.payload.inWishlist) {
            state.items.push(action.payload.wishlistItem || { _id: action.payload.productId });
          } else {
            state.items = state.items.filter(
              (item) => (item.productId || item._id) !== action.payload.productId
            );
          }
        }
      })
      .addCase(toggleWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearWishlistError } = wishlistSlice.actions;
export default wishlistSlice.reducer;
